import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, DollarSign, AlertTriangle,
  User, CalendarDays, Loader2, History,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FORMULES = {
  'appartement-1': 'Appartement 1er',
  'appartement-2': 'Appartement 2eme',
  'gite-seul': 'Gite seul',
  'gite-salle': 'Gite + Salle',
};
const fmtEUR = v => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v);

export default function AdminRefunds() {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) =>
    axios({ method, url: `${BACKEND_URL}${url}`, data, headers: { Authorization: `Bearer ${token}` } }),
  [token]);

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [approveDialog, setApproveDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { if (!token) navigate('/admin'); }, [token, navigate]);

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([
        api('get', '/api/admin/pending-refunds'),
        api('get', '/api/admin/refunds-history'),
      ]);
      setPending(p.data || []);
      setHistory(h.data || []);
    } catch { /* */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = () => {
    if (!approveDialog) return;
    const { id } = approveDialog;
    const payload = customAmount ? { custom_amount: parseFloat(customAmount) } : {};
    setApproveDialog(null);
    setCustomAmount('');
    let undone = false;
    const timer = setTimeout(async () => {
      if (undone) return;
      setProcessing(true);
      try {
        const res = await api('post', `/api/admin/pending-refunds/${id}/approve`, payload);
        toast.success(res.data.message);
        if (res.data.stripe_error) toast.warning(`Stripe: ${res.data.stripe_error}`);
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur');
      }
      setProcessing(false);
    }, 5000);
    toast.warning('Remboursement execute dans 5 secondes...', {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => { undone = true; clearTimeout(timer); toast.info('Remboursement annule — aucune action effectuee'); },
      },
    });
  };

  const handleReject = () => {
    if (!rejectDialog) return;
    const { id } = rejectDialog;
    const reason = rejectReason;
    setRejectDialog(null);
    setRejectReason('');
    let undone = false;
    const timer = setTimeout(async () => {
      if (undone) return;
      setProcessing(true);
      try {
        await api('post', `/api/admin/pending-refunds/${id}/reject`, { reason });
        toast.success('Remboursement refuse');
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur');
      }
      setProcessing(false);
    }, 5000);
    toast.warning('Refus du remboursement dans 5 secondes...', {
      duration: 5000,
      action: {
        label: 'Annuler',
        onClick: () => { undone = true; clearTimeout(timer); toast.info('Action annulee — la demande reste en attente'); },
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} data-testid="refunds-back-btn" className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
              Remboursements
            </h1>
          </div>
          {pending.length > 0 && (
            <Badge className="bg-[#C4A962] text-white border-0" data-testid="pending-refunds-count">
              {pending.length} en attente
            </Badge>
          )}
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={tab === 'pending' ? 'default' : 'outline'}
            onClick={() => setTab('pending')}
            className={`rounded-full ${tab === 'pending' ? 'bg-[#2C3E30] text-white' : ''}`}
            data-testid="tab-pending"
          >
            <Clock className="w-4 h-4 mr-1" /> En attente ({pending.length})
          </Button>
          <Button
            variant={tab === 'history' ? 'default' : 'outline'}
            onClick={() => setTab('history')}
            className={`rounded-full ${tab === 'history' ? 'bg-[#2C3E30] text-white' : ''}`}
            data-testid="tab-history"
          >
            <History className="w-4 h-4 mr-1" /> Historique ({history.length})
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : tab === 'pending' ? (
          pending.length === 0 ? (
            <div data-testid="no-pending-refunds" className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E0]">
              <CheckCircle className="w-12 h-12 text-emerald-400/50 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#595959]">Aucun remboursement en attente de validation.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map(r => (
                <div key={r.id} data-testid={`pending-refund-${r.id}`} className="bg-white rounded-2xl border border-[#E5E5E0] p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-xs">En attente</Badge>
                        <span className="text-sm text-[#595959]">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-[#595959]" strokeWidth={1.5} />
                        <span className="font-semibold text-[#2C3E30]">{r.reservation_name}</span>
                        <span className="text-sm text-[#595959]">({r.reservation_email})</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <CalendarDays className="w-4 h-4 text-[#595959]" strokeWidth={1.5} />
                        <span className="text-sm text-[#595959]">{r.arrival_date} - {r.departure_date}</span>
                        <Badge className="text-[10px] border bg-[#F5F5F0] text-[#595959]">{FORMULES[r.accommodation_type] || r.accommodation_type}</Badge>
                      </div>
                      <p className="text-sm text-[#595959] mb-1"><strong>Politique :</strong> {r.policy}</p>
                      {r.reason && <p className="text-sm text-[#595959]"><strong>Motif :</strong> {r.reason}</p>}
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-sm text-[#595959]">Paye : {fmtEUR(r.amount_paid)}</span>
                        <span className="text-sm text-[#595959]">{r.days_until_arrival}j avant arrivee</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <p className="text-2xl font-['Playfair_Display'] text-[#2C3E30]">{fmtEUR(r.refund_amount)}</p>
                        <p className="text-xs text-[#595959]">a rembourser</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`approve-refund-${r.id}`}
                          onClick={() => { setApproveDialog(r); setCustomAmount(''); }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                          size="sm"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Valider
                        </Button>
                        <Button
                          data-testid={`reject-refund-${r.id}`}
                          onClick={() => { setRejectDialog(r); setRejectReason(''); }}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50 rounded-full"
                          size="sm"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          history.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E0]">
              <History className="w-12 h-12 text-[#C4A962]/30 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#595959]">Aucun historique de remboursement.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(r => (
                <div key={r.id} data-testid={`refund-history-${r.id}`} className="bg-white rounded-xl border border-[#E5E5E0] p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs border ${r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                        {r.status === 'approved' ? 'Valide' : 'Refuse'}
                      </Badge>
                      <span className="font-medium text-sm text-[#2C3E30]">{r.reservation_name}</span>
                      <span className="text-xs text-[#595959]">{FORMULES[r.accommodation_type] || r.accommodation_type}</span>
                    </div>
                    <p className="text-xs text-[#595959]">
                      {r.arrival_date} - {r.departure_date} | {r.policy}
                      {r.reject_reason && ` | Motif refus: ${r.reject_reason}`}
                    </p>
                    <p className="text-xs text-[#595959]">Traite le {r.resolved_at ? new Date(r.resolved_at).toLocaleDateString('fr-FR') : '?'}</p>
                  </div>
                  <span className={`text-lg font-semibold ${r.status === 'approved' ? 'text-emerald-700' : 'text-red-400 line-through'}`}>
                    {fmtEUR(r.refund_amount)}
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Approve Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent className="max-w-md" data-testid="approve-refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-[#2C3E30]">Valider le remboursement</DialogTitle>
          </DialogHeader>
          {approveDialog && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-[#F5F5F0]">
                <p className="font-medium text-sm text-[#2C3E30]">{approveDialog.reservation_name}</p>
                <p className="text-xs text-[#595959]">{approveDialog.policy}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Montant a rembourser</label>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-[#2C3E30]">{fmtEUR(approveDialog.refund_amount)}</span>
                  <span className="text-sm text-[#595959]">(calcule par les CGV)</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Montant personnalise (optionnel)</label>
                <Input
                  data-testid="custom-refund-amount"
                  type="number"
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  placeholder={`Laisser vide pour ${approveDialog.refund_amount} EUR`}
                  className="rounded-lg"
                  step="0.01"
                  min="0"
                />
                <p className="text-xs text-[#595959] mt-1">Modifiez si vous souhaitez un montant different.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(null)} className="rounded-full">Annuler</Button>
            <Button data-testid="confirm-approve-btn" onClick={handleApprove} disabled={processing} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Valider le remboursement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent className="max-w-md" data-testid="reject-refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-[#2C3E30]">Refuser le remboursement</DialogTitle>
          </DialogHeader>
          {rejectDialog && (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-red-50">
                <p className="font-medium text-sm text-[#2C3E30]">{rejectDialog.reservation_name}</p>
                <p className="text-xs text-[#595959]">Montant : {fmtEUR(rejectDialog.refund_amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1C1C1E] mb-1 block">Motif du refus</label>
                <Textarea
                  data-testid="reject-reason-input"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Raison du refus..."
                  className="rounded-lg min-h-[80px]"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)} className="rounded-full">Annuler</Button>
            <Button data-testid="confirm-reject-btn" onClick={handleReject} disabled={processing} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
              {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
              Confirmer le refus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
